import { MessageBus } from '@bs/utils';
import { ARIA, CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';

/**
 * A class for the Dropdown module
 */
export default class Dropdown extends MessageBus {
  /**
   * Constructor function of the Dropdown class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Dropdown reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.items = this.element.querySelectorAll(SELECTORS.DROPDOWN_BUTTON);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.toggleDropdownHandler = this.toggleDropdown.bind(this);
    this.closeDropdownsHandler = this.closeDropdowns.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.items.forEach((item) => {
      item.addEventListener(EVENTS.CLICK, this.toggleDropdownHandler);
    });

    this.element.parentElement.addEventListener(
      EVENTS.DROPDOWN_OPENED,
      this.closeDropdownsHandler
    );

    return this;
  }

  /**
   * Handles opening and closing of a dropdown when clicked
   * @param {Event} event - the event object
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  toggleDropdown(event) {
    const button = event.target;
    const panel = button.parentElement;
    if (button.getAttribute(ARIA.EXPANDED) === MISC.FALSE) {
      button.setAttribute(ARIA.EXPANDED, MISC.TRUE);
      this.sendEvent(
        EVENTS.DROPDOWN_OPENED,
        button,
        this.element.parentElement
      );
      panel.classList.add(CLASS_NAMES.OPEN);
      setTimeout(
        () =>
          document.addEventListener(EVENTS.CLICK, this.closeDropdownsHandler),
        250
      );
    } else {
      button.setAttribute(ARIA.EXPANDED, MISC.FALSE);
      panel.classList.remove(CLASS_NAMES.OPEN);
    }

    return this;
  }

  /**
   * Closes all dropdowns except the one that was just opened
   * @param {Event} event - the event object
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  closeDropdowns(event) {
    const isDropdown =
      event.target.classList.contains('header__menu--links-child-item-url') ||
      event.target.classList.contains('header__menu--links-child-item') ||
      event.target.closest('.header__menu--links-child-item-url');

    if (!isDropdown) {
      event.preventDefault();
      const keepOpen = event.detail;
      this.items.forEach((item) => {
        if (item !== keepOpen) {
          item.parentElement.classList.remove(CLASS_NAMES.OPEN);
          item.setAttribute(ARIA.EXPANDED, MISC.FALSE);
        }
      });
      document.removeEventListener(EVENTS.CLICK, this.closeDropdownsHandler);
    }

    return this;
  }
}
